import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Product } from '@/types'

import { Seo } from '@/components/generals/seo'
import { FadeUp } from '@/components/animation/reveal'
import { AccordionItem } from '@/components/ui/accordion'
import { ShopGrid } from '@/components/shop/shop-grid'
import { ProductGallery } from '@/components/shop/product-gallery'
import { ProductBuyPanel } from '@/components/shop/product-buy-panel'
import {
    getProducts,
    getProductBySlug,
    getProductSlugs
} from '@/lib/content'

interface ProductDetailProps {
    product: Product
    others: Product[]
}

const stars = (rating: number) => {
    const filled = Math.round(rating)
    return '★'.repeat(filled) + '☆'.repeat(Math.max(0, 5 - filled))
}

export default function ProductDetail({ product, others }: ProductDetailProps) {
    const isDigital = product.category === 'Digital'
    const reviewLabel = product.reviewCount
        ? `${product.reviewCount} review${product.reviewCount === 1 ? '' : 's'}`
        : ''

    return (
        <>
            <Seo
                title={product.name}
                description={product.description}
                ogImage={product.image}
            />

            <div className='section'>
                <div className='container'>
                    <div className='w-layout-grid product-details-grid'>
                        <div
                            id='w-node-_84fcd404-2bbc-f837-b8bb-a070ae83c7f1-780953d4'
                            className='content'
                        >
                            <FadeUp distance={25}>
                                <ProductGallery
                                    images={product.images}
                                    name={product.name}
                                />
                            </FadeUp>
                        </div>

                        <div
                            id='w-node-_84fcd404-2bbc-f837-b8bb-a070ae83c7ff-780953d4'
                            className='products-template-content'
                        >
                            <div className='sticky-content product-info'>
                                <div className='product-eyebrow'>
                                    <span className='category-product'>{product.category}</span>
                                    <span className='product-stock'>
                                        <span className='product-stock-dot' /> In stock
                                    </span>
                                </div>

                                <h1 className='product-title'>{product.name}</h1>

                                {product.subtitle ? (
                                    <p className='product-subtitle'>{product.subtitle}</p>
                                ) : null}

                                {product.rating != null ? (
                                    <div className='product-rating'>
                                        <span className='product-stars' aria-hidden='true'>
                                            {stars(product.rating)}
                                        </span>
                                        <span className='product-rating-num'>
                                            {product.rating.toFixed(1)}
                                        </span>
                                        {reviewLabel ? (
                                            <span className='product-rating-count'>
                                                {reviewLabel}
                                            </span>
                                        ) : null}
                                    </div>
                                ) : null}

                                <p className='product-description'>{product.description}</p>

                                <ProductBuyPanel product={product} />

                                {product.highlights.length > 0 ? (
                                    <ul className='product-highlights'>
                                        {product.highlights.map((highlight) => (
                                            <li key={highlight}>{highlight}</li>
                                        ))}
                                    </ul>
                                ) : null}

                                <div className='product-accordions'>
                                    {product.details ? (
                                        <AccordionItem
                                            question='Details'
                                            defaultOpen
                                            answer={product.details}
                                        />
                                    ) : null}
                                    {product.materials || product.dimensions ? (
                                        <AccordionItem
                                            question='Materials & dimensions'
                                            answer={
                                                <div className='product-spec'>
                                                    {product.materials ? (
                                                        <div>
                                                            <span>Materials</span>
                                                            <p>{product.materials}</p>
                                                        </div>
                                                    ) : null}
                                                    {product.dimensions ? (
                                                        <div>
                                                            <span>Dimensions</span>
                                                            <p>{product.dimensions}</p>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            }
                                        />
                                    ) : null}
                                    <AccordionItem
                                        question='Shipping & returns'
                                        answer={
                                            isDigital
                                                ? 'Delivered instantly as a download link on the confirmation page and by email. As a digital item it is non-refundable, but reach out if anything is wrong with your files and we will make it right.'
                                                : 'Made to order and dispatched within 2–3 working days, shipped worldwide with tracking. Returns accepted within 30 days in original condition.'
                                        }
                                    />
                                </div>

                                <div className='product-meta'>
                                    <span>SKU: {product.sku || '—'}</span>
                                    <span>Free shipping over $75</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {others.length > 0 ? (
                <div className='section'>
                    <div className='container'>
                        <div className='content'>
                            <h2 className='product-details-title'>You might also like</h2>
                        </div>
                        <ShopGrid products={others} />
                    </div>
                </div>
            ) : null}
        </>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getProductSlugs()
    return {
        paths: slugs.map((slug) => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<ProductDetailProps> = async ({
    params
}) => {
    const slug = params?.slug as string
    const product = await getProductBySlug(slug)
    if (!product) return { notFound: true }

    const all = await getProducts()
    const others = all.filter((item) => item.slug !== slug).slice(0, 3)

    return { props: { product, others }, revalidate: 60 }
}
